import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { FilePdf, TrendUp, TrendDown, Scales, CalendarBlank } from "@phosphor-icons/react";

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Reports() {
  const [month, setMonth] = useState(nowMonth());
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [annual, setAnnual] = useState(null);

  useEffect(() => {
    if (!month) return;
    api.get(`/reports/tax?month=${month}`).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [month]);

  useEffect(() => {
    if (!year) return;
    api.get(`/reports/tax-annual?year=${year}`).then((r) => setAnnual(r.data)).catch(() => setAnnual(null));
  }, [year]);

  const netCls = (v) => (v >= 0 ? "text-red-600" : "text-emerald-700");

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Rapports de taxes</h1>
        <p className="text-muted-foreground mt-1">TPS/TVQ perçue moins payée = net à remettre (CTI/RTI).</p>
      </div>

      <Tabs defaultValue="monthly">
        <TabsList className="rounded-sm">
          <TabsTrigger value="monthly" data-testid="tab-monthly" className="rounded-sm"><CalendarBlank size={16} className="mr-2" /> Mensuel</TabsTrigger>
          <TabsTrigger value="annual" data-testid="tab-annual" className="rounded-sm"><Scales size={16} className="mr-2" /> Sommaire impôt (annuel)</TabsTrigger>
        </TabsList>

        {/* ---------------- Monthly ---------------- */}
        <TabsContent value="monthly" className="mt-6 space-y-6">
          <div className="flex items-end gap-2 justify-end">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Mois</Label>
              <Input data-testid="report-month" type="month" className="rounded-sm" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <Button data-testid="export-report-pdf" variant="outline" className="rounded-sm" onClick={() => window.open(`${API}/reports/tax/pdf?month=${month}&auth=${localStorage.getItem("token")}`, "_blank")}>
              <FilePdf size={18} className="mr-1" /> Exporter PDF
            </Button>
          </div>

          {report && (
            <>
              <div className="border border-border bg-card rounded-sm p-6">
                <div className="font-heading text-lg font-bold mb-1">{report.period_label}</div>
                <div className="text-sm text-muted-foreground mb-6">{report.invoices_count} facture(s) · {report.expenses_count} dépense(s)</div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><TrendUp size={16} className="text-emerald-700" /> Perçue (ventes)</div>
                    <Line label="Ventes taxables" value={report.sales_subtotal} />
                    <Line label="TPS perçue" value={report.tps_collected} />
                    <Line label="TVQ perçue" value={report.tvq_collected} />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><TrendDown size={16} className="text-blue-700" /> Payée (dépenses)</div>
                    <Line label="Dépenses" value={report.expense_subtotal} />
                    <Line label="TPS payée (CTI)" value={report.tps_paid} />
                    <Line label="TVQ payée (RTI)" value={report.tvq_paid} />
                  </div>
                  <div className="space-y-3 md:border-l md:border-border md:pl-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><Scales size={16} /> Net à remettre</div>
                    <Line label="TPS nette" value={report.net_tps} valueCls={netCls(report.net_tps)} />
                    <Line label="TVQ nette" value={report.net_tvq} valueCls={netCls(report.net_tvq)} />
                    <div className="flex justify-between border-t border-foreground pt-3 mt-1">
                      <span className="font-heading font-bold">Total net</span>
                      <span data-testid="net-total" className={`font-heading font-bold tabular-nums ${netCls(report.net_total)}`}>{money(report.net_total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-border bg-card rounded-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-border"><h2 className="font-heading font-bold">Dépenses déductibles par catégorie</h2></div>
                <Table>
                  <TableHeader><TableRow className="bg-muted/50"><TableHead>Catégorie</TableHead><TableHead className="text-right">Montant (sous-total)</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.by_category.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">Aucune dépense ce mois-ci.</TableCell></TableRow>}
                    {report.by_category.map(([cat, amt]) => (
                      <TableRow key={cat}><TableCell>{cat}</TableCell><TableCell className="text-right tabular-nums">{money(amt)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        {/* ---------------- Annual ---------------- */}
        <TabsContent value="annual" className="mt-6 space-y-6">
          <div className="flex items-end gap-2 justify-end">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Année</Label>
              <Input data-testid="report-year" type="number" min="2000" max="2100" className="rounded-sm w-32 tabular-nums" value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </div>
            <Button data-testid="export-annual-pdf" variant="outline" className="rounded-sm" onClick={() => window.open(`${API}/reports/tax-annual/pdf?year=${year}&auth=${localStorage.getItem("token")}`, "_blank")}>
              <FilePdf size={18} className="mr-1" /> Exporter le sommaire PDF
            </Button>
          </div>

          {annual && (
            <>
              <div className="border border-border bg-card rounded-sm p-6">
                <div className="font-heading text-lg font-bold mb-1">Sommaire prêt pour l'impôt — {annual.period_label}</div>
                <div className="text-sm text-muted-foreground mb-6">{annual.invoices_count} facture(s) · {annual.expenses_count} dépense(s)</div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><TrendUp size={16} className="text-emerald-700" /> Perçue (ventes)</div>
                    <Line label="Ventes taxables" value={annual.sales_subtotal} />
                    <Line label="TPS perçue" value={annual.tps_collected} />
                    <Line label="TVQ perçue" value={annual.tvq_collected} />
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><TrendDown size={16} className="text-blue-700" /> Payée (dépenses)</div>
                    <Line label="Dépenses" value={annual.expense_subtotal} />
                    <Line label="TPS payée (CTI)" value={annual.tps_paid} />
                    <Line label="TVQ payée (RTI)" value={annual.tvq_paid} />
                  </div>
                  <div className="space-y-3 md:border-l md:border-border md:pl-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-muted-foreground"><Scales size={16} /> Net annuel</div>
                    <Line label="TPS nette" value={annual.net_tps} valueCls={netCls(annual.net_tps)} />
                    <Line label="TVQ nette" value={annual.net_tvq} valueCls={netCls(annual.net_tvq)} />
                    <div className="flex justify-between border-t border-foreground pt-3 mt-1">
                      <span className="font-heading font-bold">Total net</span>
                      <span data-testid="annual-net-total" className={`font-heading font-bold tabular-nums ${netCls(annual.net_total)}`}>{money(annual.net_total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-border bg-card rounded-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <h2 className="font-heading font-bold">Dépenses déductibles par catégorie</h2>
                  <div className="text-sm text-muted-foreground">Total déductible : <span className="font-heading font-bold text-foreground tabular-nums" data-testid="annual-deductible-total">{money(annual.deductible_total)}</span></div>
                </div>
                <Table>
                  <TableHeader><TableRow className="bg-muted/50">
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="text-right">Sous-total</TableHead>
                    <TableHead className="text-right">TPS</TableHead>
                    <TableHead className="text-right">TVQ</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {annual.by_category.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Aucune dépense pour cette année.</TableCell></TableRow>}
                    {annual.by_category.map((d) => (
                      <TableRow key={d.category} data-testid={`annual-cat-${d.category}`}>
                        <TableCell>{d.category}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(d.subtotal)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{money(d.tps)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{money(d.tvq)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{money(d.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Line({ label, value, valueCls = "" }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`tabular-nums font-medium ${valueCls}`}>{money(value)}</span>
    </div>
  );
}
